import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    GuxDropdown,
    GuxListbox,
    GuxOption,
    GuxFormFieldDropdown,
} from "genesys-spark-components-react";
import {
    loadDivisions,
    setSelectedDivision,
    selectDivisions,
    selectDivisionsStatus,
    selectSelectedDivision,
    selectAreDivisionsLoaded,
    UNASSIGNED_DIVISION,
} from "./divisionsSlice";
import { useStrings } from "features/i18n/useStrings";
import strings from "./DivisionSelector.strings";
import FormType from "enums/FormType";
import { selectForm } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { toGuxDisabled } from "util/componentUtil";
import {
    qualityFormActions,
    selectIsModified,
} from "features/qualityForm/qualityFormSlice";
import { selectIsInCopyingViewMode } from "features/qualityForm/formEditor/copyFormSlice";
import { ErrorStatement } from "features/qualityForm/formEditor/common/ErrorStatement/ErrorStatement";

type DivisionSelectorProps = {
    isNewForm: boolean;
    disabled?: boolean;
    formType: FormType;
};

export function DivisionSelector({
    isNewForm,
    disabled = false,
    formType,
}: DivisionSelectorProps) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const hasInitialized = useRef(false);

    const divisions = useAppSelector(selectDivisions);
    const status = useAppSelector(selectDivisionsStatus);
    const selectedDivision = useAppSelector(selectSelectedDivision);
    const form = useAppSelector(selectForm);
    const areDivisionsLoaded = useAppSelector(selectAreDivisionsLoaded);

    const isInCopyingViewMode = useAppSelector(selectIsInCopyingViewMode);
    const isModified = useAppSelector(selectIsModified);

    // Determine the permission based on form type and whether it's new
    const permission = isNewForm
        ? `quality:${formType}Form:add`
        : `quality:${formType}Form:view`;

    // Load divisions on component mount
    useEffect(() => {
        if (status === "idle") {
            dispatch(loadDivisions(permission));
        }
    }, [dispatch, status, permission]);

    // Set default division for new forms (not copied forms)
    useEffect(() => {
        if (
            isNewForm &&
            !isInCopyingViewMode &&
            areDivisionsLoaded &&
            divisions.length > 0 &&
            !selectedDivision
        ) {
            dispatch(setSelectedDivision(UNASSIGNED_DIVISION));
            dispatch(qualityFormActions.setDivision(null));
        }
    }, [
        isNewForm,
        isInCopyingViewMode,
        areDivisionsLoaded,
        divisions.length,
        selectedDivision,
        dispatch,
    ]);

    // Sync form's division with selected division for existing forms and
    // for copies of existing forms (where form.division was preserved by the
    // copy reducer but selectedDivision may not have been initialized yet -
    // e.g. when copying from a published form whose read-only header did not
    // mount the DivisionSelector).
    useEffect(() => {
        if (
            !areDivisionsLoaded ||
            divisions.length === 0 ||
            (isNewForm && !isInCopyingViewMode)
        ) {
            return;
        }

        const formDivision = (form as any)?.division;
        const currentSelectedId = selectedDivision?.id;

        if (formDivision?.id && currentSelectedId !== formDivision.id) {
            const matchingDivision = divisions.find(
                (division) => division.id === formDivision.id
            );

            if (matchingDivision) {
                dispatch(setSelectedDivision(matchingDivision));
                dispatch(
                    qualityFormActions.setDivision({
                        id: matchingDivision.id,
                        name: matchingDivision.name,
                        selfUri: matchingDivision.selfUri,
                    })
                );
            } else {
                dispatch(setSelectedDivision(UNASSIGNED_DIVISION));
                dispatch(qualityFormActions.setDivision(null));
            }
        } else if (
            !formDivision?.id &&
            currentSelectedId !== UNASSIGNED_DIVISION.id
        ) {
            dispatch(setSelectedDivision(UNASSIGNED_DIVISION));
            dispatch(qualityFormActions.setDivision(null));
        }
    }, [
        areDivisionsLoaded,
        divisions,
        isNewForm,
        isInCopyingViewMode,
        selectedDivision?.id,
        form,
        dispatch,
    ]);

    // Initialize form modified state once divisions are loaded. Only clear
    // isModified when we're on an existing form and the form is not already
    // modified (e.g. we must not overwrite user's dirty state or new-form state).
    useEffect(() => {
        if (
            !areDivisionsLoaded ||
            hasInitialized.current ||
            isInCopyingViewMode ||
            isNewForm ||
            isModified
        ) {
            return;
        }

        dispatch(qualityFormActions.setIsFormModified({ isModified: false }));
        hasInitialized.current = true;
    }, [
        areDivisionsLoaded,
        isNewForm,
        isInCopyingViewMode,
        isModified,
        dispatch,
    ]);

    const handleDivisionChange = (
        event: React.FormEvent<HTMLGuxDropdownElement>
    ) => {
        const newDivisionId = event.currentTarget.value;
        const selectedDivision = divisions.find(
            (div) => div.id === newDivisionId
        );

        if (selectedDivision) {
            dispatch(setSelectedDivision(selectedDivision));

            // Update the form's division object
            if (selectedDivision.id !== UNASSIGNED_DIVISION.id) {
                // Set the actual division object for real divisions
                dispatch(
                    qualityFormActions.setDivision({
                        id: selectedDivision.id,
                        name: selectedDivision.name,
                        selfUri: selectedDivision.selfUri,
                    })
                );
            } else {
                // If "unassigned" is selected, set division to null (no division object in save payload)
                dispatch(qualityFormActions.setDivision(null));
            }

            // Enable save button when division changes (marks form as modified)
            dispatch(
                qualityFormActions.setIsFormModified({ isModified: true })
            );
        }
    };

    const isLoading = status === "pending";
    const hasError = status === "failed";
    const hasValidationError = false; // Division selection is now optional

    return (
        <GuxFormFieldDropdown
            className="DivisionSelector"
            data-testid="division-selector"
            label-position="above"
        >
            <label slot="label">{t.labels.divisionId}</label>
            <GuxDropdown
                filterType="starts-with"
                value={selectedDivision?.id || ""}
                onInput={handleDivisionChange}
                disabled={toGuxDisabled(disabled || isLoading)}
                aria-disabled={toGuxDisabled(disabled || isLoading)}
                placeholder={
                    isLoading ? t.labels.loading : t.labels.placeholder
                }
                hasError={hasError || hasValidationError}
            >
                <GuxListbox aria-label={t.labels.divisionOptions}>
                    {divisions.map((division) => (
                        <GuxOption key={division.id} value={division.id}>
                            {division.name}
                        </GuxOption>
                    ))}
                </GuxListbox>
            </GuxDropdown>
            {hasError && (
                <ErrorStatement errorMessage={t.errors.failedToLoad} />
            )}
        </GuxFormFieldDropdown>
    );
}
